import {
  IconBoxAlignBottom,
  IconBoxAlignLeft,
  IconBoxAlignRight,
  IconBoxAlignTop,
  IconBoxMargin,
  IconCaretRightFilled,
} from '@tabler/icons-react';
import {
  Box,
  createStyles,
  Group,
  NumberInput,
  NumberInputProps,
  Popover,
  rem,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useRichTextEditorContext } from '@mantine/tiptap';
import Button from 'components/Button';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { DropdownControlKind } from 'pages/revisions-module/template-editor/editors/template/toolbar/controls/DropdownControl';
import P2Regular from 'components/typography/P2Regular';
import P4Medium from 'components/typography/P4Medium';
import { useMemo } from 'react';
import type { Editor } from '@tiptap/react';
import ActionIcon from 'components/ActionIcon';

const useStyles = createStyles((theme) => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 110px)',
    gridTemplateRows: 'auto 130px auto',
    gap: rem(12),
    alignItems: 'center',
  },
  cell: {
    alignSelf: 'stretch',
    backgroundColor: theme.colors.gray[1],
    boxSizing: 'border-box',
  },
  icon: {
    display: 'flex',
    gap: rem(2),
    '& svg': {
      strokeWidth: '2.5 !important',
    },
  },
  item: {
    height: rem(36),
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: rem(8),
    padding: `${rem(8)} ${rem(16)}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
    '& svg': {
      width: rem(16),
      height: rem(16),
    },
  },
  chevronIcon: {
    '& path': {
      fill: theme.colors.gray[6],
    },
  },
}));

/**
 * Content of the popover used to set per-side table margins.
 */
function TableMarginForm({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const { classes } = useStyles();

  const initialValues = useMemo(() => {
    const attrs = editor.getAttributes('table');
    return {
      marginTop: Number(attrs['data-margin-top'] ?? 0),
      marginBottom: Number(attrs['data-margin-bottom'] ?? 16),
      marginLeft: Number(attrs['data-margin-left'] ?? 0),
      marginRight: Number(attrs['data-margin-right'] ?? 0),
    };
  }, []);

  const form = useForm({ initialValues });

  const previewStyle = useMemo(
    () => ({
      borderTop: `${Math.max(1, Math.abs(form.values.marginTop) / 4)}px ${
        form.values.marginTop < 0 ? 'dashed' : 'solid'
      } var(--mantine-color-gray-4)`,
      borderBottom: `${Math.max(1, Math.abs(form.values.marginBottom) / 4)}px ${
        form.values.marginBottom < 0 ? 'dashed' : 'solid'
      } var(--mantine-color-gray-4)`,
      borderLeft: `${Math.max(1, Math.abs(form.values.marginLeft) / 4)}px ${
        form.values.marginLeft < 0 ? 'dashed' : 'solid'
      } var(--mantine-color-gray-4)`,
      borderRight: `${Math.max(1, Math.abs(form.values.marginRight) / 4)}px ${
        form.values.marginRight < 0 ? 'dashed' : 'solid'
      } var(--mantine-color-gray-4)`,
    }),
    [form.values]
  );

  const inputProps: Partial<NumberInputProps> = useMemo(
    () => ({
      size: 'md',
      min: -200,
      max: 200,
      required: true,
      rightSection: <P4Medium c="gray.6">px</P4Medium>,
      styles: { required: { display: 'none' } },
    }),
    []
  );

  const onSubmit = form.onSubmit((values) => {
    editor
      .chain()
      .focus()
      .updateAttributes('table', {
        'data-margin-top': String(values.marginTop),
        'data-margin-bottom': String(values.marginBottom),
        'data-margin-left': String(values.marginLeft),
        'data-margin-right': String(values.marginRight),
      })
      .run();
    onClose();
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack spacing={24} p={16}>
        <Text size="sm" weight={500} color="gray.8">
          Odsadenie tabuľky
        </Text>
        <Box className={classes.grid}>
          <span />
          <NumberInput {...inputProps} data-autofocus icon={<IconBoxAlignTop />} {...form.getInputProps('marginTop')} />
          <span />

          <NumberInput {...inputProps} icon={<IconBoxAlignLeft />} {...form.getInputProps('marginLeft')} />
          <Box className={classes.cell} style={previewStyle} />
          <NumberInput {...inputProps} icon={<IconBoxAlignRight />} {...form.getInputProps('marginRight')} />

          <span />
          <NumberInput {...inputProps} icon={<IconBoxAlignBottom />} {...form.getInputProps('marginBottom')} />
          <span />
        </Box>

        <Group spacing={16} position="right">
          <Button type="button" onClick={onClose} variant="subtle-gray">
            Zrušiť
          </Button>
          <Button type="submit" variant="primary">
            Nastaviť
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/**
 * Sets the margin per side for the selected table.
 */
export default function TableMargin({ kind }: { kind: DropdownControlKind }) {
  const { editor } = useRichTextEditorContext();
  const [opened, { toggle, close }] = useDisclosure();
  const { classes } = useStyles();

  if (kind === 'action-icon') {
    return (
      <Popover opened={opened} onClose={close} withinPortal position="bottom-start">
        <Popover.Target>
          <Tooltip
            label="Odsadenie tabuľky"
            withArrow={false}
            openDelay={500}
            withinPortal
            position="bottom"
            disabled={opened}
          >
            <ActionIcon variant="subtle-gray" size="xs" className={classes.icon} onClick={toggle}>
              <IconBoxMargin />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown p={0}>{opened && <TableMarginForm editor={editor} onClose={close} />}</Popover.Dropdown>
      </Popover>
    );
  }

  return (
    <Popover opened={opened} onClose={close} withinPortal position="right-start">
      <Popover.Target>
        <Box className={classes.item} onClick={toggle}>
          <Box>
            <IconBoxMargin size={16} />
          </Box>
          <P2Regular c="gray.8">Odsadenie tabuľky</P2Regular>
          <IconCaretRightFilled size={12} className={classes.chevronIcon} />
        </Box>
      </Popover.Target>
      <Popover.Dropdown p={0}>{opened && <TableMarginForm editor={editor} onClose={close} />}</Popover.Dropdown>
    </Popover>
  );
}
